import React, { useCallback, useState } from "react";
import {
  FlatList,
  View,
  ViewStyle,
  FlatListProps,
  PixelRatio,
  Dimensions,
} from "react-native";
import { useScrollControl } from "../../hooks/useScrollControl";
import { FlashList } from "@shopify/flash-list";
import { useResponsive } from "../../hooks/useResponsive";

type Props<T> = FlatListProps<T> & {
  elementDistance?: number;
  style?: ViewStyle;
  hideArrows?: boolean;
  scrollRef?: React.RefObject<FlatList>;
  data: T[];
  heightOverride?: number;
};

// For content Previews, they follow this format of sizes
// use this  to calculate the height in advance to optimize the FlashList.
// for the other edge cases, i.e UpcomingRaces, they can be injected via height ovverride
const standardPaddingBothSides = 10;
const extraPixelBothSides = 2;
const standardAspectRatio = 16 / 9;
const extraVerticalPadding = 50;
const topVerticalPadding = 5;

// The window which the children will be rendered in
const visibleWidth = Dimensions.get("window").width;

const RFlatList = <T extends {}>({
  elementDistance, // width of each item + the gap to the right
  style,
  hideArrows,
  scrollRef,
  data,
  heightOverride,
  ...rest
}: Props<T>) => {
  const { isXSmallOnly } = useResponsive();
  const pixelRatio = PixelRatio.get();

  // Align width
  const rawWidth = isXSmallOnly ? 220 : 300;
  const width = Math.floor(rawWidth * pixelRatio) / pixelRatio;

  // Align height
  const height =
    (width - (standardPaddingBothSides + extraPixelBothSides)) *
      (1 / standardAspectRatio) +
    extraVerticalPadding +
    topVerticalPadding +
    extraPixelBothSides;

  const childrenWidth = elementDistance * data.length;

  const {
    handleLayout,
    handleHideArrow,
    hideLeftArrow,
    hideRightArrow,
    setPosition,
    ScrollIndicator,
  } = useScrollControl({
    scrollRef,
    elementDistance,
    childrenWidth,
  });

  const handleScroll = useCallback(
    ({ nativeEvent }) => {
      setPosition(nativeEvent.contentOffset.x);
      handleHideArrow(nativeEvent);
    },
    [setPosition, handleHideArrow]
  );

  if (rest.horizontal) {
    return (
      <View style={style}>
        {!hideArrows && !hideLeftArrow && <ScrollIndicator type="left" />}
        <FlashList
          {...rest}
          ref={scrollRef}
          data={data}
          onScroll={handleScroll}
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={200}
          estimatedItemSize={elementDistance}
          estimatedListSize={{
            width: visibleWidth,
            height:
              Math.floor((heightOverride || height) * pixelRatio) / pixelRatio,
          }}
          drawDistance={elementDistance + 500}
        />
        {!hideArrows && !hideRightArrow && <ScrollIndicator type="right" />}
      </View>
    );
  } else {
    return <FlatList style={style && style} {...rest} data={data} />;
  }
};

export default React.memo(RFlatList);
